/**
 * boot.ts — what the native shell does before React renders (APP-NATIVE-01
 * E1, 2026-09-22). Imported once from main.tsx. On the web every call here is
 * a no-op, so the office app and the customer portal never notice this file.
 *
 * DELIBERATELY NO npm IMPORTS. The Capacitor bridge injects `window.Capacitor`
 * inside the app, and every plugin whose native code `npx cap sync ios`
 * bundled is reachable as `window.Capacitor.Plugins.<Name>`. Talking to the
 * bridge directly (instead of `import "@capacitor/status-bar"`) means the web
 * build never depends on the Capacitor packages being installed — the dev
 * server on DC2022 keeps working without an `npm install`, and the plugins
 * still work in the app because the CI Mac runs `npm ci` + `cap sync`.
 * (The packages stay in package.json for exactly that reason.)
 *
 * In the app (iPhone / iPad):
 *   1. A launch at "/" becomes "/m" — the shell's only job is the tech app.
 *   2. The status bar overlays the web view; its text color follows the tech
 *      app's theme (MOBILE-LOOK-01) — see setNativeTheme().
 *   3. The splash stays up until the first paint, then fades (no white flash).
 *   4. The Android "back" gesture on the root screen leaves the app alone.
 *   5. A light haptic tap on the moments that matter — see haptic().
 */

type PluginCall = (opts?: Record<string, unknown>) => Promise<unknown>;
interface CapPlugin { [method: string]: PluginCall | undefined }
interface CapBridge {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, CapPlugin | undefined>;
}

function bridge(): CapBridge | null {
  if (typeof window === "undefined") return null;
  const c = (window as unknown as { Capacitor?: CapBridge }).Capacitor;
  return c ?? null;
}

function plugin(name: string): CapPlugin | null {
  return bridge()?.Plugins?.[name] ?? null;
}

async function call(pluginName: string, method: string, opts?: Record<string, unknown>): Promise<void> {
  try {
    const fn = plugin(pluginName)?.[method];
    if (fn) await fn(opts);
  } catch {
    /* the plugin is not in this build — fine */
  }
}

export const isNativeApp: boolean = !!bridge()?.isNativePlatform?.();

export function bootNative(): void {
  if (!isNativeApp) return;

  // 1. The app opens on the tech app, never the office shell.
  if (window.location.pathname === "/" || window.location.pathname === "") {
    window.history.replaceState(null, "", "/m");
  }

  void (async () => {
    // 2. The web view runs under the status bar; the app's own header pads for it.
    await call("StatusBar", "setOverlaysWebView", { overlay: true });
    // 3. Give React one frame to paint the header before the splash goes.
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    await call("SplashScreen", "hide", { fadeOutDuration: 200 });
    // 4. Back on the root screen: do nothing (iOS has no back button; this is
    //    here so an Android build, if one ever exists, behaves).
    try {
      const app = plugin("App");
      const add = app?.addListener as ((ev: string, cb: (e: { canGoBack?: boolean }) => void) => Promise<unknown>) | undefined;
      if (add) void add("backButton", (e) => { if (e?.canGoBack) window.history.back(); });
    } catch {
      /* no app plugin */
    }
  })();
}

/** The status bar's text follows the tech app's theme: dark text on the light
 *  glass, light text on the near-black navy. No-op on the web. */
export function setNativeTheme(dark: boolean): void {
  if (!isNativeApp) return;
  void call("StatusBar", "setStyle", { style: dark ? "DARK" : "LIGHT" });
}

/** One light tap under the thumb — On My Way, Arrived, Wrap Up, a tab change.
 *  Web: nothing (no vibration-API games in a browser). */
export function haptic(kind: "light" | "medium" | "success" = "light"): void {
  if (!isNativeApp) return;
  if (kind === "success") void call("Haptics", "notification", { type: "SUCCESS" });
  else void call("Haptics", "impact", { style: kind === "medium" ? "MEDIUM" : "LIGHT" });
}
